import { inject, observer } from "mobx-react";
import React, { useState, useCallback, useEffect } from "react";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Product } from '../../stores/product/Product';
import { RootStore } from "../../stores/rootStore";
import { getUserSelectedOpportunity, setUserSelectedOpportunity } from '../../utils/LocalStorageUtils';
import CenteredLoader from "../util/CenteredLoader";

interface IProps {
  store?: RootStore;
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AddProductToOpportunity: React.FC<IProps> = ({ store, product, open, onOpenChange }) => {
  const { applicationId } = useManufacturer();
  const [loading, setLoading] = useState(false);
  
  const opportunities = store?.opportunityStore.opportunities || [];
  const firstOpportunityBidNumber = opportunities.length > 0 ? opportunities[0].bidNumber : "";

  const [chosenOpportunity, setChosenOpportunity] = useState(getUserSelectedOpportunity() || firstOpportunityBidNumber || "");
  const [chosenPriceType, setChosenPriceType] = useState("ItemRebate");
  const [chosenBidPrice, setChosenBidPrice] = useState<number | undefined>();

  useEffect(() => {
    if (open) {
      setChosenOpportunity(getUserSelectedOpportunity() || firstOpportunityBidNumber || "");
      setChosenPriceType("ItemRebate");
      setChosenBidPrice(undefined);
    }
  }, [open, opportunities, firstOpportunityBidNumber]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleOpportunityChange = useCallback((newValue: string) => {
    setChosenOpportunity(newValue);
    setUserSelectedOpportunity(newValue || "");
  }, []);

  const handlePriceTypeChange = useCallback((newValue: string) => {
    setChosenPriceType(newValue);
  }, []);

  const handleBidPriceChange = useCallback((newValue: string) => {
    if (/^[0-9]+(\.[0-9]{0,2})?$|^$/.test(newValue)) {
      setChosenBidPrice(newValue === "" ? undefined : parseFloat(newValue));
    }
  }, []);
  
  const handleConfirmProductOpportunityRequest = useCallback(() => {
    setLoading(true);

    const { opportunityStore, snackStore, productStore } = store!;

    opportunityStore
      .addProductToOpportunity(applicationId, chosenOpportunity, product.randmarSKU || "", chosenBidPrice || 0, chosenPriceType)
      .then((success) => {
        if (success) {
          snackStore.showSuccess(`${product.mpn}. added to opportunity ${chosenOpportunity}`);
        } else {
          snackStore.showError(`Could not add product to opportunity`);
        }
        productStore.getProduct(applicationId, product.randmarSKU || "");
        setLoading(false);
        onOpenChange(false);
      });
  }, [store, applicationId, product.mpn, product.randmarSKU, chosenOpportunity, chosenPriceType, chosenBidPrice, onOpenChange]);

  const { userStore } = store!;
  const isSuperAdmin = userStore.isSuperAdmin();

  const isPriceMissing = chosenBidPrice === undefined;
  const isPriceNegative = !isPriceMissing && chosenBidPrice < 0;
  // Fallback to 0 to match original disabled logic, which prevents price > 0 if cost is not available.
  const maxPrice = product.distribution?.cost ?? 0;
  const isPriceTooHigh = !isPriceMissing && chosenBidPrice > maxPrice;

  let priceErrorMessage = "";
  if (isPriceNegative) {
    priceErrorMessage = "Amount must not be negative.";
  } else if (isPriceTooHigh && product.distribution?.cost !== undefined) {
    priceErrorMessage = `Amount must not exceed product cost ($${product.distribution.cost.toFixed(2)}).`;
  } else if (isPriceTooHigh) {
    priceErrorMessage = "Amount must be $0.00 when product cost is not available.";
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !loading) {
      onOpenChange(false);
    }
  };

  if (opportunities.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">
            Add {product.mpn} to Opportunity
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <CenteredLoader text="Please wait…" variant="plain" className="min-h-[240px]" />
        ) : (
          <>
            <form className="space-y-4">
              <div>
                <Label>Opportunity</Label>
                <Select value={chosenOpportunity} onValueChange={handleOpportunityChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select an opportunity" />
                  </SelectTrigger>
                  <SelectContent>
                    {opportunities.map(opportunity => (
                      <SelectItem key={"Opportunity" + opportunity.bidNumber} value={opportunity.bidNumber ?? ""}>
                        <b>{opportunity.bidNumber} </b> | {opportunity.opportunity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Price Type</Label>
                <Select value={chosenPriceType} onValueChange={handlePriceTypeChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a price type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key={"PriceTypeItemRebate"} value="ItemRebate">Rebate Amount</SelectItem>
                    {isSuperAdmin && <SelectItem key={"PriceTypeItemCost"} value="ItemCost">Item Cost</SelectItem>}
                    {isSuperAdmin && <SelectItem key={"PriceTypeItemCostGP"} value="ItemCostGP">Item Cost GP</SelectItem>}
                    {isSuperAdmin && <SelectItem key={"PriceTypeFixed"} value="Fixed">Fixed Pricing</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="Price">Amount</Label>
                <div className="relative mt-1.5">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                  <Input
                    id="Price"
                    placeholder="Edit amount"
                    value={chosenBidPrice ?? ''}
                    onChange={e => handleBidPriceChange(e.target.value)}
                    autoComplete="off"
                    type="number"
                    className="pl-7"
                  />
                </div>
                {priceErrorMessage && <p className="mt-1 text-sm text-red-600">{priceErrorMessage}</p>}
              </div>
            </form>
            <DialogFooter className="flex justify-between mt-4">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmProductOpportunityRequest}
                disabled={isPriceMissing || isPriceNegative || isPriceTooHigh || chosenOpportunity === "" || chosenPriceType === ""}>
                Confirm
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default inject("store")(observer(AddProductToOpportunity));
